import { routes } from "@/global/routes";
import {
  faqs,
  howItWorks,
  StLouisCardinalsCheckoutTerms,
  StLouisCardinalsAdditionalDetailsDisclaimerTC,
} from "./helpers";
import { inventoryTypeUIDs, vendorUUIDs } from "../../constants";
import { DefaultRegistrationTerms } from "../../default_terms_and_conditions";

import LogoImage from "public/images/stlouiscardinals_suites/logo.png";
import BackgroundImage from "public/images/stlouiscardinals_suites/venue.jpg";
import SeatMap3dPreview from "public/images/diamondbacks/seat_map_preview.png";
import Banner from "public/images/stlouiscardinals_suites/AttendPageheader_1486x148.jpg";
import SuiteImage1 from "public/images/stlouiscardinals_suites/counter graphic- All Suites.jpg";
import SuiteImage2 from "public/images/stlouiscardinals_suites/Suite looking - 300 Level Suites Only.jpg";
import SuiteImage3 from "public/images/stlouiscardinals_suites/suite looking out (004).png";

import type { TeamConfig } from "../types";

const config: TeamConfig = {
  teamSchema: "stlouiscardinals_sandbox",
  browserTitle: "St. Louis Cardinals",
  packages: false,
  defaultRoute: routes.purchaseSeats,
  phoneNumber: "",
  mlbTeamId: 138,
  teamName: "St. Louis Cardinals",

  // seat map configs
  seatMapConfigs: {
    venueId: "",
    mapId: "",

    hide3dViewFromSlider: true,
    staticSeatMapImage: SeatMap3dPreview.src,
    suiteImages: [SuiteImage1.src, SuiteImage2.src, SuiteImage3.src],
  },

  // gift cards
  giftCardConfigs: {
    isMenuGiftCardEnabled: false,
    isLandingPageGiftCardEnabled: false,
    isStandalonePageGiftCardEnabled: false,
    redirectGiftCardUrl: routes.redeem,
  },

  // purchase credit configs
  purchaseCreditConfigs: {
    isLandingPurchaseCreditEnabled: false,
    isStandAlonePurchaseCreditEnabled: false,
    showPurchaseCreditLinkInHomeMenu: false,
    isPurchaseCreditHistoryEnabled: false,
  },

  // disabled routes
  disabledRoutes: [],

  // switches
  checkoutCreditPackages: false,
  availableCreditEnabled: false,
  isCrowdHandlerEnabled: true,
  inventoryType: inventoryTypeUIDs.suiteLevelV2,

  // purchase seats config
  purchaseSeatConfigs: {
    minGameSelectionCount: 1,
    minSeatLimit: 1,
    isSimilarSeatForAllEvents: false,
    allowManualCartInSimilar: false,
    isCheckoutEditSeatsEnabled: false,
    isDiffSeatAlertEnabled: false,
    hideViewTicketsInThankYouPage: true,

    isCheckoutAdditionalDetailsEnabled: true,
    isPerksEnabled: true,
  },

  // feature configs
  packageConfigs: {
    isPackageEnabled: false,
    canRemovePackageGames: false,
    staticBuildYourOwn: null,
  },

  // intercom settings
  intercomConfigs: {
    isIntercomEnabled: true,
    allowedPages: [],
  },

  // checkout validations
  checkoutValidation: {
    allowedZipFailCount: Infinity,
    allowedPaymentFailCount: Infinity,
  },

  // analytics configs
  analyticsConfigs: {
    googleAnalyticsId: "",
    attrkPixelId: "",
    beaconId: "",
    googleTagManagerId: "",
  },

  // redirect configs
  redirectConfigs: {
    internalRedirectUrl: "",
    externalRedirectUrl: "",
  },

  refreshInventoryWhenEventClick: false,

  isSaveCardAllowed: false,

  // urls
  accountManagerUrl: "https://www.mlb.com/cardinals",

  // vendor
  platform: vendorUUIDs.tickesdotcom,

  // okta config
  oktaIssuer: "https://integrator-3404041.okta.com", // dev okta creds
  oktaClientId: "0oayigvuniVNS5aTO697", // dev okta creds
  shouldUseInternalLogin: false,

  isBrainTreePaymentEnabled: false,

  // images
  images: {
    findGamesBannerImage: Banner.src,
    authBackgroundImage: BackgroundImage.src,
    checkoutStatusPageImage: BackgroundImage.src,
    giftCardStandalonePageImage: Banner.src,
    logoImage: LogoImage.src + "?width=128&height=66",
    logoNameImage: "",
    faviconImage: LogoImage.src,
    seatMap3dPreviewImage: SeatMap3dPreview.src,
  },

  uiConfigs: {
    bannerImage: {
      title: "",
      isBackgroundBasedBanner: false,
    },

    featuredGameCard: {
      showLogoWithTitle: false,
      hideOverlayLogo: true,
    },
    bottomBar: {
      showGameSelectionProgress: true,
    },

    hidePackageBalanceOnCheckout: false,
    hideServiceFee: false,
    shouldDisplayServiceFee: false,
    shouldAddServiceFeeWithPerkPrice: true,

    inventoryCardSettings: {
      isPBLineItemsVisible: true,
      seatCountLabel: "Tickets Included",
      inventorySuitePackageInfo: "$600",
      pbLineItemsValue: 5,
      hideFBPackageLineItem: true,
    },
    inventoryContainer: {
      getGridCellHeight: (arg) => (arg?.mdDown ? 240 : 150),
    },
    checkoutPerkSubtotals: {
      ticketTotalLabel: "Total Ticket Balance",
      serviceFeeLabel: "Order Service Fee",
    },
  },

  colors: {
    primary: "#C41E3A",
  },

  howItWorks: howItWorks,
  faqs: faqs,

  languages: {
    topNavMenuItems: null,
    checkoutTermsAndCondition: <StLouisCardinalsCheckoutTerms />,
    registrationTermsAndCondition: <DefaultRegistrationTerms />,
    additionalDetailsDisclaimerTC: (
      <StLouisCardinalsAdditionalDetailsDisclaimerTC />
    ),
    purchaseSeats: {
      progressBarSuccessText:
        "You're good to go! Add more games or select Continue to view seating options.",
      progressBarAditionalText: " to continue!",
    },
    purchaseGiftCard: null,
    redeemGiftCard: null,

    checkoutSuccessMessage: "Your purchase is complete!",
    checkoutSuccessDescription:
      "Your reservation is not yet complete until notification from the St. Louis Cardinals confirming your reservation has been accepted in accordance with our agreement.",

    giftCard: {
      title: "",
      createGiftCard: {
        description: "",
        recipient: {
          sendNowFalse: {
            description: "",
          },
          sendNowTrue: {
            description: "",
          },
        },
      },
      redeemGiftCard: {
        loggedIn: {
          description: "",
        },
        logout: {
          description: "",
        },
        success: {
          description: "",
        },
      },
    },

    signInAuthText:
      "Connect your mlb.com account to continue with seamless access.",
  },
};

export default config;
